/* eslint-disable no-use-before-define */
// ignore the above rule in favor of grouping the types together

// GRAPH TYPES
const AGGREGATE_DISABLES_AREA_OPTION = aggregate => {
  switch (aggregate) {
    case ASSET_AGGREGATE.name:
    case COMPANY_BY_ASSET_AGGREGATE.name:
    case DEVELOPMENT_STATUS_AGGREGATE.name:
      return true;
    default:
      return false;
  }
};
const AREA_OPTIONS_DISABLED = ({ aggregate, graphType }) => {
  // Option is disabled if the selected graph type is not the parent
  if (AREA.value !== graphType.value) return true;
  // If aggregate is string (coming from bar charts) check if aggregate is compatible with option
  if (typeof aggregate === 'string') {
    return AGGREGATE_DISABLES_AREA_OPTION(aggregate);
  } else {
    // If aggregate is object (coming from tree maps) check if any of the aggregate layers are compatible with option
    return !!Object.values(aggregate).find(agg => AGGREGATE_DISABLES_AREA_OPTION(agg.name));
  }
};
export const AREA = {
  label: 'Area',
  value: 'Area',
  isDisabled: ({ aggregate }) =>
    [
      YEAR_AGGREGATE.name,
      ASSET_AGGREGATE.name,
      COMPANY_BY_ASSET_AGGREGATE.name,
      DEVELOPMENT_STATUS_AGGREGATE.name,
    ].includes(aggregate),
  optionIsDisabled: AREA_OPTIONS_DISABLED,
  resetGraphTypeOptions: ({ aggregate, graphType, graphTypeOptions }) => {
    let options = graphTypeOptions.filter(option =>
      graphTypeChildren.Area.find(child => child.key === option.key),
    );

    if (graphType?.value === AREA.value) {
      options = graphTypeChildren.Area;
    }
    if (AGGREGATE_DISABLES_AREA_OPTION(aggregate)) {
      options = [];
    }
    return options;
  },
};
const BREAKEVENS = {
  label: 'Breakevens',
  value: 'Breakevens',
  isDisabled: ({ aggregate }) => aggregate !== ASSET_AGGREGATE.name,
};
const ABEX = { label: 'Abandonment Cost', value: 'Abandonment Cost' };
const CAPEX = { label: 'Capex (excl. Abandonment)', value: 'Capex' };
const CUMULATIVE_CAPEX = {
  label: 'Cumulative capex (excl. Abandonment)',
  value: 'Cumulative capex',
};
export const CUMULATIVE_DISCOUNTED_CASH_FLOW = {
  label: 'Cumulative Discounted Cash Flow',
  value: 'Cumulative Discounted Cash Flow',
};

export const DISCOUNTED_CASH_FLOW = {
  label: 'Discounted Cash Flow',
  value: 'Discounted Cash Flow',
};
export const ESTIMATED_CURRENT_PRODUCTION_BOED = {
  label: 'Estimated Current Production',
  optionLabel: 'boe/d',
  value: 'Estimated Current Production boed',
  resetGraphTypeOptions: ({ graphTypeOptions }) =>
    graphTypeOptions.filter(
      option =>
        option.name === 'Entitlement' ||
        graphTypeChildren['Estimated Current Production boed']?.find(
          child => child.key === option.key,
        ),
    ),
  optionIsDisabled: ({ graphType }) => {
    return graphType?.value !== ESTIMATED_CURRENT_PRODUCTION_BOED.value;
  },
};
export const ESTIMATED_CURRENT_PRODUCTION_BBLD = {
  label: 'Estimated Current Production',
  optionLabel: 'bbl/d',
  value: 'Estimated Current Production bbld',
};
export const ESTIMATED_CURRENT_PRODUCTION_MMCFD = {
  label: 'Estimated Current Production',
  optionLabel: 'MMcf/d',
  value: 'Estimated Current Production mmcfd',
};
const ESTIMATED_CURRENT_PRODUCTION = {
  label: 'Estimated Current Production',
  isDisabled: ({ aggregate }) => aggregate === YEAR_AGGREGATE.name,
  optionIsDisabled: ({ graphType }) => {
    // Disable Entitlement option when bbl/d or MMcf/d is selected
    const nonBoeOptions = [
      ESTIMATED_CURRENT_PRODUCTION_BBLD.value,
      ESTIMATED_CURRENT_PRODUCTION_MMCFD.value,
    ];
    return nonBoeOptions.includes(graphType?.value);
  },
  resetGraphTypeOptions: ({ graphTypeOptions, graphType }) => {
    // Preserve Entitlement if it was selected (it's now controlled by checkbox aggregate)
    // Only preserve Entitlement if boe/d is selected, otherwise clear it
    if (graphType?.value === ESTIMATED_CURRENT_PRODUCTION_BOED.value) {
      // Preserve Entitlement if it exists, but don't add it if it doesn't
      return graphTypeOptions.filter(option => option.name === 'Entitlement');
    }
    // For other production types, clear Entitlement
    return graphTypeOptions.filter(option => option.name !== 'Entitlement');
  },
  options: [
    ESTIMATED_CURRENT_PRODUCTION_BOED,
    ESTIMATED_CURRENT_PRODUCTION_BBLD,
    ESTIMATED_CURRENT_PRODUCTION_MMCFD,
  ],
};
export const NPV10 = {
  label: 'NPV10',
  optionLabel: 'NPV10',
  value: 'NPV10',
};
const NPV15 = {
  label: 'NPV15',
  optionLabel: 'NPV15',
  value: 'NPV15',
};
const NPV20 = {
  label: 'NPV20',
  optionLabel: 'NPV20',
  value: 'NPV20',
};
const NPV25 = {
  label: 'NPV25',
  optionLabel: 'NPV25',
  value: 'NPV25',
};
export const VALUATIONS = {
  label: 'Valuations',
  value: 'Valuations',
  options: [NPV10, NPV15, NPV20, NPV25],
  isDisabled: ({ aggregate }) => aggregate === YEAR_AGGREGATE.name,
  optionIsDisabled: ({ graphType }) => {
    return graphType?.value !== VALUATIONS.value;
  },
};
const OPEX = { label: 'Opex', value: 'Opex' };
const PROVED_DEVELOPED_PRODUCTION = {
  label: 'Proved Developed Production',
  value: 'Proved Developed Production',
  isDisabled: ({ checkboxAggregates }) => checkboxAggregates[PDP_AND_PUD_CHECKBOX_AGGREGATE.name],
  optionIsDisabled: ({ aggregate, graphType }) =>
    aggregate === COMPANY_AGGREGATE.name ||
    aggregate === DEVELOPMENT_STATUS_AGGREGATE.name ||
    PROVED_DEVELOPED_PRODUCTION.value !== graphType.value,
  resetGraphTypeOptions: ({ aggregate, graphTypeOptions }) =>
    aggregate === COMPANY_AGGREGATE.name || aggregate === DEVELOPMENT_STATUS_AGGREGATE.name
      ? []
      : graphTypeOptions.filter(option =>
          graphTypeChildren[PROVED_DEVELOPED_PRODUCTION.value].find(
            child => child.key === option.key,
          ),
        ),
};
const PRE_TAX_CASH_FLOW = { label: 'Pre-Tax Cash Flow', value: 'Pre-Tax Cash Flow' };
const REMAINING_CAPEX = { label: 'Remaining CAPEX', value: 'Remaining CAPEX' };
export const TOTAL_PRODUCTION = {
  label: 'Total Production',
  value: 'Total Production',
  optionIsDisabled: ({ aggregate, graphType }) =>
    aggregate === COMPANY_AGGREGATE.name ||
    aggregate === DEVELOPMENT_STATUS_AGGREGATE.name ||
    TOTAL_PRODUCTION.value !== graphType.value,
  resetGraphTypeOptions: ({ aggregate, graphTypeOptions }) => {
    if (aggregate === COMPANY_AGGREGATE.name || aggregate === DEVELOPMENT_STATUS_AGGREGATE.name) {
      return [];
    }
    // Preserve Entitlement if it was selected (it's now in graphTypeChildren for LineGraph)
    return graphTypeOptions.filter(
      option =>
        option.name === 'Entitlement' ||
        graphTypeChildren[TOTAL_PRODUCTION.value].find(child => child.key === option.key),
    );
  },
};
const UNDEVELOPED_PRODUCTION = {
  label: 'Undeveloped Production',
  value: 'Undeveloped Production',
  isDisabled: ({ checkboxAggregates }) => checkboxAggregates[PDP_AND_PUD_CHECKBOX_AGGREGATE.name],
};

export const REMAINING_RESERVES = {
  label: 'Reserves',
  value: 'Reserves',
  optionIsDisabled: ({ graphType }) => 'Reserves' !== graphType.value,
  resetGraphTypeOptions: ({ graphType, graphTypeOptions }) => {
    if (graphType?.value === REMAINING_RESERVES.value) {
      // Only preserve options that were already selected
      // Preserve Entitlement if it was selected (it's now controlled by checkbox aggregate)
      // Also preserve options from graphTypeChildren
      return graphTypeOptions.filter(
        option =>
          option.name === 'Entitlement' ||
          graphTypeChildren.Reserves.find(child => child.key === option.key),
      );
    }

    const selectedOptions = graphTypeOptions.filter(
      option =>
        option.name === 'Entitlement' ||
        graphTypeChildren.Reserves.find(child => child.key === option.key),
    );

    return selectedOptions;
  },
};
export const REMAINING_OIL_AND_CONDENSATE_RESERVES = {
  label: 'Oil and Condensate Reserves',
  value: 'Oil and Condensate Reserves',
  optionIsDisabled: ({ graphType }) => {
    return graphType?.value !== REMAINING_OIL_AND_CONDENSATE_RESERVES.value;
  },
  resetGraphTypeOptions: ({ graphTypeOptions }) => {
    // Preserve Entitlement if it was selected (it's now controlled by checkbox aggregate)
    return graphTypeOptions.filter(option => option.name === 'Entitlement');
  },
};
export const REMAINING_GAS_RESERVES = {
  label: 'Gas Reserves',
  value: 'Gas Reserves',
  options: [
    {
      label: 'Gas Reserves',
      optionLabel: 'Bcf',
      value: 'Gas Reserves Bcf',
    },
    {
      label: 'Gas Reserves',
      optionLabel: 'Bm³',
      value: 'Gas Reserves Bcm',
    },
  ],
  optionIsDisabled: ({ graphType }) => {
    // Enable options when Gas Reserves parent is selected OR when either child option is selected
    const gasReservesValues = [
      REMAINING_GAS_RESERVES.value,
      REMAINING_GAS_RESERVES.options[0].value,
      REMAINING_GAS_RESERVES.options[1].value,
    ];
    return !gasReservesValues.includes(graphType?.value);
  },
  resetGraphTypeOptions: ({ graphTypeOptions, graphType }) => {
    // Preserve Entitlement if it was selected (it's now controlled by checkbox aggregate)
    const gasReservesValues = [
      REMAINING_GAS_RESERVES.value,
      REMAINING_GAS_RESERVES.options[0].value,
      REMAINING_GAS_RESERVES.options[1].value,
    ];

    // If switching between Gas Reserves options, preserve Entitlement if it exists
    if (gasReservesValues.includes(graphType?.value)) {
      return graphTypeOptions.filter(option => option.name === 'Entitlement');
    }

    return [];
  },
};

export const WELL_COUNT = {
  label: 'Well Count',
  value: 'Well Count',
  isDisabled: ({ aggregate }) =>
    // TODO: https://welligence.atlassian.net/browse/XWWP-2950 - add support for ASSET_AGGREGATE.name, COUNTRY_AGGREGATE.name
    ![YEAR_AGGREGATE.name].includes(aggregate),
  optionIsDisabled: ({ graphType }) => 'Well Count' !== graphType.value,
  resetGraphTypeOptions: ({ graphTypeOptions }) =>
    graphTypeOptions.filter(option =>
      graphTypeChildren['Well Count'].find(child => child.key === option.key),
    ),
  tooltip: `Can only be selected with year aggregate.`,
};

export const monetaryGraphTypes = [
  OPEX,
  CAPEX,
  CUMULATIVE_CAPEX,
  ABEX,
  DISCOUNTED_CASH_FLOW,
  CUMULATIVE_DISCOUNTED_CASH_FLOW,
  PRE_TAX_CASH_FLOW,
];

export const productionGraphTypes = [
  TOTAL_PRODUCTION,
  PROVED_DEVELOPED_PRODUCTION,
  UNDEVELOPED_PRODUCTION,
];

export const treeMapTypes = [
  ESTIMATED_CURRENT_PRODUCTION,
  REMAINING_CAPEX,
  REMAINING_RESERVES,
  REMAINING_OIL_AND_CONDENSATE_RESERVES,
  REMAINING_GAS_RESERVES,
  AREA,
  // TODO: Do we want these for the treemap eventually?
  // https://trello.com/c/Bz2pElIO/2248-portfolio-analysis-remaining-reserves
  // 'Remaining PDP Oil and Condensate Reserves',
  // 'Remaining PDP Gas Reserves',
  // 'Remaining PUD Oil and Condensate Reserves',
  // 'Remaining PUD Gas Reserves',
];

export const barGraphTypes = [
  ESTIMATED_CURRENT_PRODUCTION,
  VALUATIONS,
  BREAKEVENS,
  REMAINING_CAPEX,
  REMAINING_RESERVES,
  AREA,
];

export const featureFlaggedBarGraphTypes = [
  ESTIMATED_CURRENT_PRODUCTION,
  VALUATIONS,
  BREAKEVENS,
  REMAINING_CAPEX,
  REMAINING_RESERVES,
  AREA,
  WELL_COUNT,
];

export const INCLUDE_NON_MODELLED_PRODUCTION_GRAPH_TYPE_OPTION = {
  key: 'Include Non-Modelled Production',
  name: 'Include Non-Modelled Production',
  tooltip:
    'Can only be selected with country or asset aggregate. The use of filters will also affect the presence of this data.',
};
export const HYDROCARBON_GRAPH_TYPE_OPTION = {
  key: 'Hydrocarbon',
  name: 'Hydrocarbon',
};
export const PRODUCTION_GRAPH_TYPE_OPTION = {
  key: 'PDP / Undeveloped',
  name: 'PDP / Undeveloped',
};
export const ENTITLEMENT_GRAPH_TYPE_OPTION = {
  key: 'Entitlement',
  name: 'Entitlement',
};

// Mutual exclusivity groups - options in the same group cannot be selected together
export const MUTUALLY_EXCLUSIVE_OPTION_GROUPS = [
  [PRODUCTION_GRAPH_TYPE_OPTION.name, ENTITLEMENT_GRAPH_TYPE_OPTION.name],
  [INCLUDE_NON_MODELLED_PRODUCTION_GRAPH_TYPE_OPTION.name, ENTITLEMENT_GRAPH_TYPE_OPTION.name],
];

/**
 * Enforces mutual exclusivity between options in the same group.
 * Only processes when there's an actual conflict to avoid unnecessary processing.
 */
export const enforceMutualExclusivity = (
  newOptions = [],
  previousOptions = [],
  exclusiveGroups = MUTUALLY_EXCLUSIVE_OPTION_GROUPS,
) => {
  const newOpts = Array.isArray(newOptions) ? newOptions : [];
  const prevOpts = Array.isArray(previousOptions) ? previousOptions : [];
  let filteredOptions = [...newOpts];

  // Check each mutual exclusivity group
  exclusiveGroups.forEach(group => {
    const optionsInGroup = filteredOptions.filter(option => group.includes(option.name));

    // Only process if there's actually a conflict (multiple options from same group)
    if (optionsInGroup.length > 1) {
      const previousInGroup = prevOpts.filter(option => group.includes(option.name));
      const previousNames = previousInGroup.map(opt => opt.name);

      // Find which option is new (wasn't in previous state)
      const newlyAdded = optionsInGroup.find(opt => !previousNames.includes(opt.name));

      if (newlyAdded) {
        // A new option was added, remove the others from this group
        filteredOptions = filteredOptions.filter(
          option => !group.includes(option.name) || option.name === newlyAdded.name,
        );
      } else {
        // Both were already selected - keep the one that appears last (most recently clicked)
        const lastOption = [...filteredOptions]
          .reverse()
          .find(option => group.includes(option.name));
        if (lastOption) {
          filteredOptions = filteredOptions.filter(
            option => !group.includes(option.name) || option.name === lastOption.name,
          );
        }
      }
    }
  });

  return filteredOptions;
};

const EXPLORATION_GRAPH_TYPE_OPTION = {
  key: 'Exploration Area',
  name: 'Exploration',
  tooltip: 'Can only be selected with country and/or company aggregate.',
};
const DEVELOPMENT_GRAPH_TYPE_OPTION = {
  key: 'Development Area',
  name: 'Development',
  tooltip: 'Can only be selected with country and/or company aggregate.',
};

const APPRAISAL_WELL_GRAPH_TYPE_OPTION = {
  key: 'Appraisal Wells',
  name: 'Appraisal',
  tooltip: `Can only be selected with year aggregate.`,
};

const EXPLORATION_WELL_GRAPH_TYPE_OPTION = {
  key: 'Exploration Wells',
  name: 'Exploration',
  tooltip: `Can only be selected with year aggregate.`,
};

const PRODUCER_WELL_GRAPH_TYPE_OPTION = {
  key: 'Producer Wells',
  name: 'Producer',
  tooltip: `Can only be selected with year aggregate.`,
};

const INJECTOR_WELL_GRAPH_TYPE_OPTION = {
  key: 'Injector Wells',
  name: 'Injector',
  tooltip: `Can only be selected with year aggregate.`,
};

const OTHER_WELL_GRAPH_TYPE_OPTION = {
  key: 'Other Wells',
  name: 'Other',
  tooltip: `Can only be selected with year aggregate.`,
};

export const graphTypeChildren = {
  [PROVED_DEVELOPED_PRODUCTION.value]: [INCLUDE_NON_MODELLED_PRODUCTION_GRAPH_TYPE_OPTION],
  [TOTAL_PRODUCTION.value]: [INCLUDE_NON_MODELLED_PRODUCTION_GRAPH_TYPE_OPTION],
  [REMAINING_RESERVES.value]: [HYDROCARBON_GRAPH_TYPE_OPTION, PRODUCTION_GRAPH_TYPE_OPTION],
  [ESTIMATED_CURRENT_PRODUCTION.value]: [],
  [ESTIMATED_CURRENT_PRODUCTION_BOED.value]: [],
  [AREA.value]: [EXPLORATION_GRAPH_TYPE_OPTION, DEVELOPMENT_GRAPH_TYPE_OPTION],
  [WELL_COUNT.value]: [
    EXPLORATION_WELL_GRAPH_TYPE_OPTION,
    APPRAISAL_WELL_GRAPH_TYPE_OPTION,
    PRODUCER_WELL_GRAPH_TYPE_OPTION,
    INJECTOR_WELL_GRAPH_TYPE_OPTION,
    OTHER_WELL_GRAPH_TYPE_OPTION,
  ],
};

// Custom graphTypeChildren for LineGraph - includes Entitlement under Total Production
export const lineGraphTypeChildren = {
  ...graphTypeChildren,
  [TOTAL_PRODUCTION.value]: [
    INCLUDE_NON_MODELLED_PRODUCTION_GRAPH_TYPE_OPTION,
    ENTITLEMENT_GRAPH_TYPE_OPTION,
  ],
};
export const treeGraphTypeChildren = {
  [REMAINING_RESERVES.value]: [],
  [REMAINING_OIL_AND_CONDENSATE_RESERVES.value]: [],
  [REMAINING_GAS_RESERVES.options[0].value]: [], // Gas Reserves Bcf
  [REMAINING_GAS_RESERVES.options[1].value]: [], // Gas Reserves Bcm
  [ESTIMATED_CURRENT_PRODUCTION.value]: [],
  [ESTIMATED_CURRENT_PRODUCTION_BOED.value]: [],
  [AREA.value]: [EXPLORATION_GRAPH_TYPE_OPTION, DEVELOPMENT_GRAPH_TYPE_OPTION],
};

// Helper function to filter out Entitlement from graphTypeChildren based on feature flag
// Used for LineGraph which has Entitlement in lineGraphTypeChildren
export const getFilteredGraphTypeChildren = (children, entitlementEnabled) => {
  if (entitlementEnabled === true) {
    return children;
  }
  const filtered = {};
  Object.keys(children).forEach(key => {
    filtered[key] = (children[key] || []).filter(option => option.name !== 'Entitlement');
  });
  return filtered;
};

// HYDROCARBON TYPES
export const ALL = { label: 'All', value: 'All' };
export const OIL = {
  label: 'Oil',
  value: 'Oil',
  isDisabled: checkboxAggregates => checkboxAggregates[HYDROCARBON_CHECKBOX_AGGREGATE.name],
};
export const GAS = {
  label: 'Gas (MMcf/d)',
  value: 'Gas',
  isDisabled: checkboxAggregates => checkboxAggregates[HYDROCARBON_CHECKBOX_AGGREGATE.name],
};
export const GAS_MMCM = {
  label: 'Gas (MMm³/d)',
  value: 'Gas MMCF',
  isDisabled: checkboxAggregates => checkboxAggregates[HYDROCARBON_CHECKBOX_AGGREGATE.name],
};
export const CONDENSATE = {
  label: 'Condensate',
  value: 'Condensate',
  isDisabled: checkboxAggregates => checkboxAggregates[HYDROCARBON_CHECKBOX_AGGREGATE.name],
};
export const OIL_AND_CONDENSATE = {
  label: 'Oil and Condensate',
  value: 'Oil and Condensate',
  isDisabled: checkboxAggregates => checkboxAggregates[HYDROCARBON_CHECKBOX_AGGREGATE.name],
  optionIsDisabled: ({ graphType }) => {
    return graphType?.value !== OIL_AND_CONDENSATE.value;
  },
};
export const hydrocarbonTypes = [ALL, OIL, GAS, GAS_MMCM, CONDENSATE, OIL_AND_CONDENSATE];

export const graphTitles = {
  [ESTIMATED_CURRENT_PRODUCTION.value]: '(boe/d)',
  [ESTIMATED_CURRENT_PRODUCTION_BOED.value]: '(boe/d)',
  [ESTIMATED_CURRENT_PRODUCTION_BBLD.value]: '(bbl/d)',
  [ESTIMATED_CURRENT_PRODUCTION_MMCFD.value]: '(MMcf/d)',
  [NPV10.value]: '(US$MM)',
  [NPV15.value]: '(US$MM)',
  [NPV20.value]: '(US$MM)',
  [NPV25.value]: '(US$MM)',
  [BREAKEVENS.value]: '(US$/bbl)',
  [REMAINING_CAPEX.value]: '(US$MM)',
  [REMAINING_RESERVES.value]: '(MMboe)',
  [REMAINING_OIL_AND_CONDENSATE_RESERVES.value]: '(MMbbl)',
  [REMAINING_GAS_RESERVES.options[0].value]: '(Bcf)',
  [REMAINING_GAS_RESERVES.options[1].value]: '(Bm³)',
  [ALL.value]: '(boe/d)',
  [OIL.value]: 'Oil (bbl/d)',
  [GAS.value]: 'Gas (MMcf/d)',
  [GAS_MMCM.value]: 'Gas (MMm³/d)',
  [CONDENSATE.value]: 'Condensate (bbl/d)',
  [OIL_AND_CONDENSATE.value]: 'Oil and Condensate (bbl/d)',
  [AREA.value]: '(km²)',
  [WELL_COUNT.value]: '',
};

const checkboxAggregateSelected = (checkboxAggregates = {}) => {
  // Exclude 'Entitlement' from this check since it's a data filter, not an aggregate control
  const { Entitlement: _Entitlement, ...otherCheckboxAggregates } = checkboxAggregates;
  return !!Object.values(otherCheckboxAggregates).find(agg => agg);
};

// AGGREGATE TYPES
export const COUNTRY_AGGREGATE = {
  name: 'Country',
  options: true,
  isDisabled: ({ checkboxAggregates = {}, graphType = {} }) =>
    checkboxAggregateSelected(checkboxAggregates) ||
    graphType.value === BREAKEVENS.value ||
    graphType.value === WELL_COUNT.value,
};

export const ASSET_AGGREGATE = {
  name: 'Asset',
  options: true,
  isDisabled: ({ checkboxAggregates = {}, graphTypeOptions = [], graphType = {} }) =>
    !!graphTypeOptions.find(option =>
      graphTypeChildren.Area.find(child => child.key === option.key),
    ) ||
    checkboxAggregateSelected(checkboxAggregates) ||
    graphType.value === WELL_COUNT.value,
};
export const COMPANY_AGGREGATE = {
  name: 'Company',
  options: true,
  isDisabled: ({ checkboxAggregates = {}, graphType = {} }) =>
    checkboxAggregateSelected(checkboxAggregates) ||
    graphType.value === BREAKEVENS.value ||
    graphType.value === WELL_COUNT.value,
};

const COMPANY_BY_ASSET_AGGREGATE = {
  name: 'Company Participation by Asset',
  options: true,
  isDisabled: ({ checkboxAggregates = {}, graphTypeOptions = [], graphType = {} }) =>
    !!graphTypeOptions.find(option =>
      graphTypeChildren.Area.find(child => child.key === option.key),
    ) ||
    checkboxAggregateSelected(checkboxAggregates) ||
    graphType.value === BREAKEVENS.value ||
    graphType.value === WELL_COUNT.value,
};

const DEVELOPMENT_STATUS_AGGREGATE = {
  name: 'Development Status',
  aggregate: 'Development Status',
  isDisabled: ({ checkboxAggregates = {}, graphTypeOptions = [], graphType = {} }) =>
    !!graphTypeOptions.find(option =>
      graphTypeChildren.Area.find(child => child.key === option.key),
    ) ||
    checkboxAggregateSelected(checkboxAggregates) ||
    graphType.value === BREAKEVENS.value ||
    graphType.value === WELL_COUNT.value,
};

const YEAR_AGGREGATE = {
  name: 'Year',
  aggregate: 'Year',
  isDisabled: ({ graphType = {} }) =>
    [
      AREA.value,
      BREAKEVENS.value,
      ESTIMATED_CURRENT_PRODUCTION_BOED.value,
      ESTIMATED_CURRENT_PRODUCTION_BBLD.value,
      ESTIMATED_CURRENT_PRODUCTION_MMCFD.value,
      NPV10.value,
      NPV15.value,
      NPV20.value,
      NPV25.value,
    ].includes(graphType.value),
};

export const PDP_AND_PUD_CHECKBOX_AGGREGATE = {
  name: 'PDP / Undeveloped',
  aggregate: 'PDP / Undeveloped',
  isDisabled: ({ graphType }) => graphType.value !== TOTAL_PRODUCTION.value,
  tooltip: `Can only be selected when '${TOTAL_PRODUCTION.label}' metric is selected`,
  type: 'checkbox',
};

export const HYDROCARBON_CHECKBOX_AGGREGATE = {
  name: 'Hydrocarbon',
  aggregate: 'Hydrocarbon',
  isDisabled: ({ hydrocarbonType }) => hydrocarbonType !== ALL.label,
  tooltip: `Can only be selected when '${ALL.label}' hydrocarbon types is selected`,
  type: 'checkbox',
};

// Metrics that support entitlement filtering
const ENTITLEMENT_SUPPORTED_METRICS = [
  ESTIMATED_CURRENT_PRODUCTION_BOED.value, // Only boed supports entitlement for production
  TOTAL_PRODUCTION.value, // Total Production supports entitlement
  REMAINING_RESERVES.value,
  REMAINING_OIL_AND_CONDENSATE_RESERVES.value,
  REMAINING_GAS_RESERVES.options[0]?.value, // Gas Reserves Bcf
  REMAINING_GAS_RESERVES.options[1]?.value, // Gas Reserves Bcm
].filter(value => value !== undefined);

export const ENTITLEMENT_CHECKBOX_AGGREGATE = {
  name: 'Entitlement',
  aggregate: 'Entitlement',
  isDisabled: ({ graphType }) => {
    const graphTypeValue = graphType?.value ?? '';
    return !ENTITLEMENT_SUPPORTED_METRICS.includes(graphTypeValue);
  },
  tooltip: ({ graphType }) => {
    const graphTypeValue = graphType?.value ?? '';
    const isSupported = ENTITLEMENT_SUPPORTED_METRICS.includes(graphTypeValue);

    if (isSupported) {
      return 'Display entitlement production';
    }

    const isNonBoeProduction =
      graphTypeValue === ESTIMATED_CURRENT_PRODUCTION_BBLD.value ||
      graphTypeValue === ESTIMATED_CURRENT_PRODUCTION_MMCFD.value;

    if (isNonBoeProduction) {
      return 'Entitlement is only available for boe/d. Use boe/d to view entitlement data for production metrics.';
    }

    return 'Can only be selected for production (boe/d) and reserves metrics';
  },
  type: 'checkbox',
  // Special flag to indicate this should sync with graphTypeOptions
  syncsWithGraphTypeOptions: true,
};

export const defaultAggregateTypes = [
  COUNTRY_AGGREGATE,
  ASSET_AGGREGATE,
  COMPANY_AGGREGATE,
  DEVELOPMENT_STATUS_AGGREGATE,
];

export const treemapAggregateTypes = [...defaultAggregateTypes, ENTITLEMENT_CHECKBOX_AGGREGATE];

export const barChartAggregateTypes = [
  COUNTRY_AGGREGATE,
  ASSET_AGGREGATE,
  COMPANY_AGGREGATE,
  COMPANY_BY_ASSET_AGGREGATE,
  DEVELOPMENT_STATUS_AGGREGATE,
  ENTITLEMENT_CHECKBOX_AGGREGATE,
];

export const featureFlaggedBarChartAggregateTypes = [
  COUNTRY_AGGREGATE,
  ASSET_AGGREGATE,
  COMPANY_AGGREGATE,
  COMPANY_BY_ASSET_AGGREGATE,
  DEVELOPMENT_STATUS_AGGREGATE,
  YEAR_AGGREGATE,
  ENTITLEMENT_CHECKBOX_AGGREGATE,
];

// Helper function to filter out ENTITLEMENT_CHECKBOX_AGGREGATE from aggregate types based on feature flag
export const getFilteredAggregateTypes = (aggregateTypes, entitlementEnabled) => {
  if (entitlementEnabled === true) {
    return aggregateTypes;
  }
  return aggregateTypes.filter(aggregate => aggregate.name !== 'Entitlement');
};

export const productionAggregateTypes = [
  ...defaultAggregateTypes,
  HYDROCARBON_CHECKBOX_AGGREGATE,
  PDP_AND_PUD_CHECKBOX_AGGREGATE,
  ENTITLEMENT_CHECKBOX_AGGREGATE,
];

// Production aggregate types for LineGraph - excludes Entitlement (it's in SELECT METRIC section)
export const lineGraphProductionAggregateTypes = [
  ...defaultAggregateTypes,
  HYDROCARBON_CHECKBOX_AGGREGATE,
  PDP_AND_PUD_CHECKBOX_AGGREGATE,
];

export const getTodaysDate = () => {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth());
};
